/**
 * React hook: derive the set of leaf scene-object ids from the current
 * object-mode selection.
 *
 * `selectedObjectTargets` is the authoritative selection state, but many
 * UI consumers and renderers still need a flat `Set<string>` of leaf ids
 * to ask "is this object selected?" — for outline passes, hit proxies,
 * hotkey handlers, and similar.
 *
 * The derivation is cached at module level keyed on the identity of the
 * three input slices, so every viewport node that calls this hook shares
 * one Set instance per selection state. Without the shared cache, dozens
 * of nodes would each rebuild the same Set on every selection change
 * (and on every blendFiles / collections mutation), which dominated
 * fan-out cost during selection-driven re-renders.
 *
 * Prefer subscribing to `selectedObjectTargets.length > 0` for simple
 * "anything selected" checks; use this hook only when you actually need
 * the leaf id set.
 */

import { selectionToLeafObjectIds } from "./objectSelection.ts";
import { useStore } from "./store.ts";
import type { AppState } from "./storeTypes.ts";

const EMPTY_LEAF_IDS: ReadonlySet<string> = new Set<string>();

type CacheEntry = {
  targets: AppState["selectedObjectTargets"];
  blendFiles: AppState["blendFiles"];
  collections: AppState["collections"];
  result: ReadonlySet<string>;
};

let cached: CacheEntry | null = null;

/**
 * Pure selector — same identity output for the same input identities,
 * so `useStore`'s default `Object.is` equality avoids spurious re-renders
 * across all consumers.
 *
 * Return type is `ReadonlySet<string>`: the returned Set is shared across
 * every consumer via the module-level cache, so mutating it would corrupt
 * the shared state. The TS type makes `.add` / `.delete` / `.clear` a
 * compile-time error.
 */
export function getSelectedLeafObjectIds(state: AppState): ReadonlySet<string> {
  // Empty-selection short-circuit: returns the same empty Set forever so
  // the common "nothing selected" case never trips a re-render even when
  // blendFiles / collections mutate.
  if (state.selectedObjectTargets.length === 0) {
    return EMPTY_LEAF_IDS;
  }
  if (
    cached &&
    cached.targets === state.selectedObjectTargets &&
    cached.blendFiles === state.blendFiles &&
    cached.collections === state.collections
  ) {
    return cached.result;
  }
  const result = selectionToLeafObjectIds(
    state.blendFiles,
    state.collections,
    state.selectedObjectTargets,
  );
  cached = {
    targets: state.selectedObjectTargets,
    blendFiles: state.blendFiles,
    collections: state.collections,
    result,
  };
  return result;
}

export function useSelectedLeafObjectIds(): ReadonlySet<string> {
  return useStore(getSelectedLeafObjectIds);
}

/** Test-only: drop the module-level cache between store instances. */
export function __resetSelectedLeafObjectIdsCache(): void {
  cached = null;
}
